import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { agentService } from '../../services/agentService';
import { adminService } from '../../services/adminService';
import { isAdmin } from '../../services/auth';
import './MajesticModal.css';

const UserSupportModal = ({ show, onHide, user, onSuccess }) => {
    const { t } = useTranslation();
    const [message, setMessage] = useState(user?.supportMessage || '');
    const [loading, setLoading] = useState(false);
    const [globalMessage, setGlobalMessage] = useState('');

    useEffect(() => {
        if (show && isAdmin()) {
            fetchGlobalMessage();
        }
        // Reset message when user changes
        setMessage(user?.supportMessage || '');
    }, [show, user]);

    const fetchGlobalMessage = async () => {
        try {
            const response = await adminService.getGlobalSettings();
            if (response.success) {
                setGlobalMessage(response.settings?.globalSupportMessage || '');
            }
        } catch (error) {
            console.warn('Could not fetch global settings:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await agentService.updateUser(user.id, {
                supportMessage: message
            });

            if (response.success) {
                toast.success(t('support_message_saved', 'Support message saved successfully!'));
                if (onSuccess) onSuccess();
                onHide();
            }
        } catch (error) {
            toast.error(t(error.message || 'support_message_failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleClearCustomMessage = async () => {
        setLoading(true);
        try {
            const response = await agentService.updateUser(user.id, {
                supportMessage: ''
            });

            if (response.success) {
                setMessage('');
                toast.success(t('custom_message_cleared', 'Custom message cleared - user will see global message'));
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            toast.error(t(error.message || 'clear_message_failed'));
        } finally {
            setLoading(false);
        }
    };

    const hasCustomMessage = user?.supportMessage && user.supportMessage.trim() !== '';

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            contentClassName="majestic-modal-content border-0 shadow-lg"
            backdrop="static"
        >
            <div className="majestic-modal-content overflow-hidden">
                <Modal.Header closeButton className="support-modal-header border-0 py-4">
                    <Modal.Title className="text-white fw-bold d-flex align-items-center gap-3">
                        <div className="bg-warning bg-opacity-10 p-2 rounded-3 shadow-sm border border-warning border-opacity-10">
                            <i className="bi bi-chat-left-dots-fill text-warning"></i>
                        </div>
                        <span className="majestic-text-gradient fs-5 letter-spacing-1">{t('support_console', 'SUPPORT CONSOLE')}</span>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="p-4">
                    <div className="mb-4 d-flex align-items-center justify-content-between p-3 rounded-4 support-info-box">
                        <div className="d-flex align-items-center">
                            <div className="text-secondary small me-2 opacity-50">{t('messaging_user', 'MESSAGING USER:')}</div>
                            <div className="fw-bold text-white letter-spacing-1 fs-6">
                                {user?.username}
                            </div>
                        </div>
                        {hasCustomMessage && (
                            <span className="majestic-badge-warning">
                                <i className="bi bi-star-fill me-1"></i>
                                {t('custom_override', 'Custom')}
                            </span>
                        )}
                    </div>

                    {/* Global Message Reference - Only for Admin */}
                    {isAdmin() && globalMessage && (
                        <div className="mb-4 p-3 rounded-4 support-global-box border-opacity-25">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="bg-primary bg-opacity-10 p-1 rounded-2">
                                        <i className="bi bi-globe-americas text-primary small"></i>
                                    </div>
                                    <span className="fw-bold text-primary opacity-90 small letter-spacing-1">{t('current_global_message', 'GLOBAL MESSAGE')}</span>
                                </div>
                                {!hasCustomMessage && (
                                    <span className="majestic-badge-blue">
                                        {t('active_for_user', 'Active')}
                                    </span>
                                )}
                            </div>
                            <div className="support-message-display">
                                <p className="text-white text-opacity-75 small mb-0 lh-base" style={{ maxHeight: '80px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    <i className="bi bi-quote me-1 text-primary opacity-50"></i>
                                    {globalMessage}
                                </p>
                            </div>
                        </div>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-4">
                            <Form.Label className="text-secondary small fw-bold mb-2 text-uppercase letter-spacing-1 opacity-75">
                                {t('custom_message_for_user', 'Custom Message for This User')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                placeholder={t('type_message_here', 'Write your official support message here...')}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                style={{ minHeight: '160px', lineHeight: '1.6' }}
                                className="majestic-input"
                            />
                            <div className="form-text text-muted small mt-2 d-flex align-items-center gap-2 opacity-75">
                                <i className="bi bi-info-circle text-warning border-0" style={{ fontSize: '14px' }}></i>
                                <span className="text-white text-opacity-75">{t('overrides_global', 'This message will override the global support message')}</span>
                            </div>
                        </Form.Group>

                        <div className="d-flex justify-content-between align-items-center pt-2">
                            {/* Clear Custom Button - Only show if has custom message */}
                            {hasCustomMessage ? (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleClearCustomMessage}
                                    disabled={loading}
                                    className="d-flex align-items-center gap-2 border-0 bg-secondary bg-opacity-10 py-2 px-3 rounded-3"
                                >
                                    <i className="bi bi-arrow-counterclockwise text-secondary"></i>
                                    <span className="text-secondary small fw-bold">{t('use_global', 'Use Global')}</span>
                                </Button>
                            ) : <div></div>}

                            <div className="d-flex align-items-center gap-3">
                                <Button
                                    variant="secondary"
                                    onClick={onHide}
                                    className="px-4 py-2 border-0 bg-white bg-opacity-5 hover-bg-opacity-10 transition-all rounded-3 fw-bold small"
                                >
                                    {t('cancel')}
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 majestic-pulse-glow"
                                >
                                    {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : t('save_message', 'UPDATE MESSAGE')}
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Modal.Body>
            </div>
        </Modal>
    );
};

export default UserSupportModal;
